import { X, Settings, Variable, MessageSquare, Database, FileText } from 'lucide-react';

interface MetadataPanelProps {
  botSettings: any;
  flowVariables: Record<string, any[]>;
  intents: any[];
  knowledgeBase: any[];
  documents: any[];
  onClose: () => void;
}

export default function MetadataPanel({
  botSettings,
  flowVariables,
  intents,
  knowledgeBase,
  documents,
  onClose,
}: MetadataPanelProps) {
  // Safe defaults
  const safeFlowVars = flowVariables || {};
  const safeIntents = intents || [];
  const safeKB = knowledgeBase || [];
  const safeDocs = documents || [];
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Database className="w-7 h-7" />
            Botpress Metadata
          </h2>
          <button
            onClick={onClose}
            className="hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Bot Settings */}
          {botSettings && (
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border-2 border-blue-300">
              <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Bot Settings
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><strong>Version:</strong> {botSettings.version}</div>
                <div><strong>Bot ID:</strong> {botSettings.botId.substring(0, 12)}...</div>
                <div><strong>Languages:</strong> {botSettings.languages.join(', ')}</div>
                <div><strong>Timeout:</strong> {botSettings.inactivityTimeout}s</div>
                <div><strong>Node Repeat Limit:</strong> {botSettings.nodeRepetitionLimit}</div>
                <div className="col-span-2">
                  <strong>Best Model:</strong> {botSettings.defaultBestModel || 'N/A'}
                </div>
                <div className="col-span-2">
                  <strong>Fast Model:</strong> {botSettings.defaultFastModel || 'N/A'}
                </div>
              </div>
            </div>
          )}

          {/* Flow Variables */}
          {Object.keys(safeFlowVars).length > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl border-2 border-green-300">
              <h3 className="text-lg font-bold text-green-900 mb-3 flex items-center gap-2">
                <Variable className="w-5 h-5" />
                Flow Variables
              </h3>
              {Object.entries(safeFlowVars).map(([flowName, vars]) => (
                <div key={flowName} className="mb-4">
                  <div className="font-semibold text-green-800 mb-2">Flow: {flowName}</div>
                  <div className="space-y-2">
                    {vars.map((v: any) => (
                      <div key={v.id} className="bg-white p-2 rounded border border-green-200 text-sm">
                        <strong>{v.name}</strong>: <span className="text-gray-600">{v.type}</span>
                        {v.defaultValue && <span className="ml-2 text-gray-500">= {v.defaultValue}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Intents */}
          {safeIntents.length > 0 && (
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-4 rounded-xl border-2 border-yellow-300">
              <h3 className="text-lg font-bold text-yellow-900 mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Intents ({safeIntents.length})
              </h3>
              <div className="space-y-3">
                {safeIntents.map((intent: any) => (
                  <div key={intent.id} className="bg-white p-3 rounded border border-yellow-200">
                    <div className="font-semibold text-yellow-800 mb-1">{intent.name}</div>
                    <div className="text-xs text-gray-600">
                      {intent.utterances_with_slots?.slice(0, 5).join(', ')}
                      {intent.utterances_with_slots?.length > 5 && '...'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Knowledge Base */}
          {safeKB.length > 0 && (
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-xl border-2 border-orange-300">
              <h3 className="text-lg font-bold text-orange-900 mb-3 flex items-center gap-2">
                <Database className="w-5 h-5" />
                Knowledge Base ({safeKB.length})
              </h3>
              <div className="space-y-3">
                {safeKB.map((kb: any, idx: number) => (
                  <div key={idx} className="bg-white p-3 rounded border border-orange-200">
                    <div className="font-semibold text-orange-800 mb-2">
                      Data Sources: {kb.dataSources?.length || 0}
                    </div>
                    {kb.dataSources?.map((ds: any) => (
                      <div key={ds.id} className="text-xs text-gray-600 mb-1">
                        • <strong>{ds.title || ds.type}</strong> ({ds.type})
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          {safeDocs.length > 0 && (
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-xl border-2 border-purple-300">
              <h3 className="text-lg font-bold text-purple-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents ({safeDocs.length})
              </h3>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {safeDocs.map((doc: any) => (
                  <div key={doc.id} className="bg-white p-2 rounded border border-purple-200 text-xs">
                    <div className="font-semibold text-purple-800">{doc.label}</div>
                    <div className="text-gray-600 mt-1">
                      Type: {doc.metadata?.type || 'unknown'} | 
                      Chunks: {doc.chunks || 0} |
                      Size: {Math.round((doc.size || 0) / 1024)}KB
                    </div>
                    {doc.metadata?.pageUrl && (
                      <div className="text-blue-600 truncate mt-1">{doc.metadata.pageUrl}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

